// Create / rename dialog for a My List: Name (required) + Description (optional).
import { useEffect, useRef, useState } from 'react';
import { getMyListErrorMessage } from '../../services/myListsService';
import styles from './ListNameModal.module.css';

export const LIST_NAME_MAX = 80;
export const LIST_DESCRIPTION_MAX = 500;

export const VISIBILITY_CHOICES = [
    { value: 'private', label: 'Private', hint: 'Only you can see this list.' },
    { value: 'public', label: 'Public', hint: 'Anyone can view this list from your profile or shared link.' },
];

export default function ListNameModal({
    isOpen,
    mode = 'create',            // 'create' | 'rename'
    initialName = '',
    initialDescription = '',
    initialVisibility = 'private', // lists are private unless the user picks Public
    showVisibility = true,
    onSubmit,                   // async ({ name, description, visibility }) => void
    onClose,
}) {
    const [name, setName] = useState(initialName);
    const [description, setDescription] = useState(initialDescription);
    const [visibility, setVisibility] = useState(initialVisibility);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const submittingRef = useRef(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;
        setName(initialName);
        setDescription(initialDescription);
        setVisibility(initialVisibility === 'public' ? 'public' : 'private');
        setError('');
        setSaving(false);
        submittingRef.current = false;
        const handleKey = (e) => { if (e.key === 'Escape' && !submittingRef.current) onClose(); };
        window.addEventListener('keydown', handleKey);
        const focus = setTimeout(() => inputRef.current?.focus(), 0);
        return () => { window.removeEventListener('keydown', handleKey); clearTimeout(focus); };
    }, [isOpen, initialName, initialDescription, initialVisibility, onClose]);

    if (!isOpen) return null;

    const trimmedName = name.trim();
    const canSubmit = trimmedName.length > 0 && trimmedName.length <= LIST_NAME_MAX && !saving;

    async function handleSubmit(e) {
        e.preventDefault();
        if (!canSubmit || submittingRef.current) return; // double-submit guard
        submittingRef.current = true;
        setSaving(true);
        setError('');
        try {
            await onSubmit({ name: trimmedName, description: description.trim() || null, visibility: showVisibility ? visibility : undefined });
        } catch (err) {
            setError(getMyListErrorMessage(err));
            submittingRef.current = false;
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className={styles.backdrop} onClick={saving ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="list-name-modal-title">
            <form className={styles.panel} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
                <h2 id="list-name-modal-title" className={styles.title}>{mode === 'rename' ? 'Rename List' : 'Create New List'}</h2>

                <label className={styles.label} htmlFor="list-name-input">Name</label>
                <input
                    id="list-name-input"
                    ref={inputRef}
                    className={styles.input}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Anime I Need to Watch"
                    maxLength={LIST_NAME_MAX}
                    disabled={saving}
                    required
                />

                <label className={styles.label} htmlFor="list-description-input">
                    Description <span className={styles.optional}>(optional)</span>
                </label>
                <textarea
                    id="list-description-input"
                    className={styles.textarea}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What's this list for?"
                    maxLength={LIST_DESCRIPTION_MAX}
                    rows={3}
                    disabled={saving}
                />

                {showVisibility && (
                    <fieldset className={styles.fieldset} disabled={saving}>
                        <legend className={styles.label}>Visibility</legend>
                        {VISIBILITY_CHOICES.map((choice) => (
                            <label key={choice.value} className={`${styles.choice} ${visibility === choice.value ? styles.choiceActive : ''}`}>
                                <input type="radio" name="list-visibility" value={choice.value} checked={visibility === choice.value} onChange={() => setVisibility(choice.value)} />
                                <span className={styles.choiceBody}>
                                    <span className={styles.choiceLabel}>{choice.label}</span>
                                    <span className={styles.choiceHint}>{choice.hint}</span>
                                </span>
                            </label>
                        ))}
                    </fieldset>
                )}

                {error && <div className={styles.error} role="alert">{error}</div>}

                <div className={styles.actions}>
                    <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={saving}>Cancel</button>
                    <button type="submit" className={styles.primaryBtn} disabled={!canSubmit}>
                        {saving ? 'Saving…' : mode === 'rename' ? 'Save' : 'Create List'}
                    </button>
                </div>
            </form>
        </div>
    );
}
